// Sampling line items editor — image, product name, qty, charges.
import React from "react";
import { Trash2, Image as ImageIcon, Plus, Camera } from "lucide-react";
import { compressImageFile } from "../lib/image";
import { materialsSubtotal } from "./MaterialsEditor";

export function newSamplingItem() {
  return {
    id: Math.random().toString(36).slice(2, 10),
    image: null,
    product_name: "",
    num_samples: "1",
    pattern_base: "",
    pattern_grading: "",
    grading_sizes: "",
    sampling_charges: "",
  };
}

export function computeSamplingTotals(items, materials = [], gstRate = 5) {
  let itemsSubtotal = 0;
  items.forEach((it) => {
    const numSamples = Number(it.num_samples || 1);
    const samplingCharges = Number(it.sampling_charges || 0) * numSamples;
    const patternBase = Number(it.pattern_base || 0);
    const gradingPerSize = Number(it.pattern_grading || 0);
    const gradingSizes = Number(it.grading_sizes || 0);
    const grading = gradingPerSize * gradingSizes;
    itemsSubtotal += samplingCharges + patternBase + grading;
  });
  const mats = materialsSubtotal(materials);
  const subtotal = itemsSubtotal + mats;
  const gst = (subtotal * gstRate) / 100;
  return { itemsSubtotal, mats, subtotal, gst, total: subtotal + gst };
}

async function fileToDataUrl(file) {
  return compressImageFile(file);
}

export function SamplingItemsEditor({ items, materials = [], onChange, gstRate = 5 }) {
  const update = (id, patch) => onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const remove = (id) => onChange(items.filter((it) => it.id !== id));
  const add = () => onChange([...items, newSamplingItem()]);

  const onPickImage = async (id, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      alert("Image must be under 15 MB");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    update(id, { image: dataUrl });
  };

  const totals = computeSamplingTotals(items, materials, gstRate);

  return (
    <div>
      {items.map((it, idx) => (
        <div key={it.id} style={cardStyle} data-testid={`sampling-item-${idx}`}>
          <div style={rowHead}>
            <span style={itemNo}>Style #{idx + 1}</span>
            {items.length > 1 && (
              <button onClick={() => remove(it.id)} style={removeBtn} data-testid={`sampling-remove-${idx}`}>
                <Trash2 size={14} color="var(--danger)" />
              </button>
            )}
          </div>

          <div style={imageSlot} data-testid={`sampling-image-${idx}`}>
            {it.image ? (
              <img src={it.image} alt="product" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div style={imgPh}>
                <ImageIcon size={22} color="var(--muted)" />
                <span style={{ fontSize: 12, color: "var(--muted)" }}>Add a product photo</span>
              </div>
            )}
          </div>
          <div style={imgBtnRow}>
            <label style={imgBtn} data-testid={`sampling-camera-${idx}`}>
              <Camera size={14} color="var(--brand)" />
              <span style={imgBtnText}>Camera</span>
              <input type="file" accept="image/*" capture="environment" onChange={(e) => onPickImage(it.id, e)} style={{ display: "none" }} />
            </label>
            <label style={imgBtn} data-testid={`sampling-gallery-${idx}`}>
              <ImageIcon size={14} color="var(--brand)" />
              <span style={imgBtnText}>Gallery</span>
              <input type="file" accept="image/*" onChange={(e) => onPickImage(it.id, e)} style={{ display: "none" }} />
            </label>
          </div>

          <label className="sg-label">Product name</label>
          <input className="sg-input" value={it.product_name || ""} onChange={(e) => update(it.id, { product_name: e.target.value })} placeholder="e.g. Linen co-ord set" />

          <div style={row2}>
            <div style={col}>
              <label className="sg-label">No. samples</label>
              <input className="sg-input" value={it.num_samples || ""} onChange={(e) => update(it.id, { num_samples: e.target.value })} placeholder="1" inputMode="numeric" />
            </div>
            <div style={col}>
              <label className="sg-label">Sampling / sample ₹</label>
              <input className="sg-input" value={it.sampling_charges || ""} onChange={(e) => update(it.id, { sampling_charges: e.target.value })} placeholder="0" inputMode="numeric" />
            </div>
          </div>
          <div style={row2}>
            <div style={col}>
              <label className="sg-label">Pattern (Base) ₹</label>
              <input className="sg-input" value={it.pattern_base || ""} onChange={(e) => update(it.id, { pattern_base: e.target.value })} placeholder="0" inputMode="numeric" />
            </div>
            <div style={col}>
              <label className="sg-label">Grading / size ₹</label>
              <input className="sg-input" value={it.pattern_grading || ""} onChange={(e) => update(it.id, { pattern_grading: e.target.value })} placeholder="0" inputMode="numeric" />
            </div>
          </div>
          <div style={row2}>
            <div style={col}>
              <label className="sg-label">No. of sizes graded</label>
              <input className="sg-input" value={it.grading_sizes || ""} onChange={(e) => update(it.id, { grading_sizes: e.target.value })} placeholder="0" inputMode="numeric" />
            </div>
            <div style={col} />
          </div>
        </div>
      ))}
      <button type="button" onClick={add} style={addBtn} data-testid="sampling-add-item">
        <Plus size={16} color="var(--brand)" />
        <span style={{ color: "var(--brand)", fontWeight: 700 }}>Add another style</span>
      </button>

      <div style={summaryBox}>
        <Row label="Styles subtotal" value={fmt(totals.itemsSubtotal)} />
        {materials.length > 0 && <Row label="Materials subtotal" value={fmt(totals.mats)} />}
        <Row label="Subtotal" value={fmt(totals.subtotal)} />
        <Row label={`GST @ ${gstRate}%`} value={fmt(totals.gst)} />
        <div style={{ borderTop: "1px solid var(--line)", marginTop: 8, paddingTop: 10 }}>
          <Row label="Total" value={fmt(totals.total)} big />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, big }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
      <span style={{ fontSize: big ? 14 : 13, color: big ? "var(--brand)" : "var(--muted)", fontWeight: big ? 800 : 600 }}>{label}</span>
      <span style={{ fontSize: big ? 20 : 14, color: big ? "var(--brand)" : "var(--ink)", fontWeight: big ? 800 : 600 }}>{value}</span>
    </div>
  );
}

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const cardStyle = { background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12, padding: 14, marginBottom: 12 };
const rowHead = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 };
const itemNo = { fontSize: 11, fontWeight: 800, color: "var(--brand)", textTransform: "uppercase", letterSpacing: 0.6 };
const removeBtn = { width: 30, height: 30, borderRadius: 8, background: "var(--surface-subtle)", border: 0, cursor: "pointer", display: "grid", placeItems: "center" };
const imageSlot = { display: "block", height: 140, background: "var(--surface-subtle)", borderRadius: 10, overflow: "hidden", border: "1px dashed var(--line)", marginBottom: 8 };
const imgPh = { width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 };
const imgBtnRow = { display: "flex", gap: 8, marginBottom: 12 };
const imgBtn = { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 10, background: "var(--surface)", cursor: "pointer" };
const imgBtnText = { fontSize: 12, fontWeight: 700, color: "var(--brand)" };
const row2 = { display: "flex", gap: 8 };
const col = { flex: 1 };
const addBtn = { display: "flex", alignItems: "center", gap: 6, justifyContent: "center", width: "100%", padding: 12, border: "1px dashed var(--line)", borderRadius: 10, background: "var(--surface)", cursor: "pointer", marginBottom: 14 };
const summaryBox = { background: "var(--surface-subtle)", borderRadius: 12, padding: 14, marginBottom: 8 };
